import { getTextContentFromHTML } from "@/lib";
import parse from "html-react-parser";
import { useState } from "react";
import * as styles from "./TruncatedText.css";

interface Props {
	text: string;
	wordLimit?: number;
	labels?: [string, string | undefined];
}

export default function TruncatedText({
	text,
	wordLimit = 30,
	labels = ["read more", "read less"],
}: Props) {
	const [isExpanded, setIsExpanded] = useState(false);
	const [collapsedLabel, expandedLabel] = labels;

	const getTruncatedText = (text: string, wordLimit: number): string => {
		// Regular expression to match sentences (includes trailing punctuation).
		const sentenceRegex = /[^.!?]+[.!?]+[\])'"`’”]*/g;
		const sentences = text.match(sentenceRegex);

		// If no sentence boundaries are found, fallback to splitting by words.
		if (!sentences) {
			const words = text.split(" ");
			return words.slice(0, wordLimit).join(" ").trim();
		}

		let truncated = "";
		let count = 0;

		for (const sentence of sentences) {
			const sentenceWords = sentence
				.split(" ")
				.filter((word) => word.length > 0);
			// Check if adding this sentence would exceed the word limit.
			if (count + sentenceWords.length > wordLimit) {
				// If no sentence has been added (i.e., first sentence is too long), fallback to word-based truncation.
				if (count === 0) {
					truncated = sentenceWords.slice(0, wordLimit).join(" ");
				}
				break;
			}
			truncated += sentence;
			count += sentenceWords.length;
		}
		return truncated.trim();
	};

	const truncatedText = getTruncatedText(text, wordLimit);

	const isTruncated =
		getTextContentFromHTML(text.trim()).trim() !==
		getTextContentFromHTML(truncatedText).trim();

	const toggleExpand = () => {
		setIsExpanded((prev) => !prev);
	};

	return (
		<div className={styles.container}>
			{parse(isExpanded || !isTruncated ? text : `${truncatedText}`)}
			{isTruncated && (
				<button type="button" onClick={toggleExpand} className={styles.button}>
					{isExpanded ? (expandedLabel ?? collapsedLabel) : collapsedLabel}
				</button>
			)}
		</div>
	);
}
